import React, { useState, useEffect, useRef } from "react";
import { db, functions } from "../firebase";
import { collection, query, orderBy, onSnapshot, addDoc, getDocs, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { Send2, Link2, Notification, Add, Repeat, SearchNormal1 } from "iconsax-reactjs";
import "./NotificationPanel.css";

// Audience values are sent to the sendNotification function as-is; only the
// button labels are shortened.
const SEGMENTS = [
  { value: "All Registered Users", label: "All users" },
  { value: "Tenants Only", label: "Tenants" },
  { value: "Dealers Only", label: "Dealers" },
];

const segmentLabel = (value) => SEGMENTS.find((s) => s.value === value)?.label || value || "All users";

const MESSAGE_LIMIT = 120;
// Lock screens start cutting titles off around here: a guide, not a cap.
const TITLE_GUIDE = 50;
const HISTORY_PAGE = 20;

const formatCount = (n) => n.toLocaleString("en-IN");

// A user's push tokens, read the way sendNotification reads them: the
// fcmTokens array, or the legacy single fcmToken field.
function userTokens(data) {
  if (Array.isArray(data.fcmTokens)) return data.fcmTokens.filter(Boolean);
  return data.fcmToken ? [data.fcmToken] : [];
}

export default function NotificationPanel() {
  const [devices, setDevices] = useState(null); // { total, android, ios } unique tokens
  const [audienceDevices, setAudienceDevices] = useState(null); // { dealers, tenants } or { failed }
  const [campaignTitle, setCampaignTitle] = useState("");
  const [targetSegment, setTargetSegment] = useState("All Registered Users");
  const [body, setBody] = useState("");
  const [deepLink, setDeepLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historySearch, setHistorySearch] = useState("");
  const [historyShown, setHistoryShown] = useState(HISTORY_PAGE);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const composerRef = useRef(null);
  const titleInputRef = useRef(null);

  // 1. Devices that can get a push: unique tokens in fcm_tokens, which is
  //    exactly what an "All users" send goes to.
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "fcm_tokens"), (snapshot) => {
      const platforms = new Map();
      snapshot.forEach((d) => {
        const { token, platform } = d.data();
        if (token) platforms.set(token, platform);
      });
      let android = 0;
      let ios = 0;
      platforms.forEach((platform) => {
        if (platform === "android") android++;
        else if (platform === "ios") ios++;
      });
      setDevices({ total: platforms.size, android, ios });
    }, (error) => {
      console.error("Error reading FCM token stats:", error);
    });

    return () => unsubscribe();
  }, []);

  // 2. Devices per audience, counted once: tenant and dealer sends go to the
  //    tokens on each user's doc, a missing role counting as tenant, the same
  //    split sendNotification makes.
  useEffect(() => {
    let cancelled = false;
    getDocs(collection(db, "users"))
      .then((snapshot) => {
        const dealers = new Set();
        const tenants = new Set();
        snapshot.forEach((d) => {
          const data = d.data();
          const audience = (data.role || "tenant") === "dealer" ? dealers : tenants;
          userTokens(data).forEach((token) => audience.add(token));
        });
        if (!cancelled) setAudienceDevices({ dealers: dealers.size, tenants: tenants.size });
      })
      .catch((error) => {
        console.error("Error counting devices per audience:", error);
        if (!cancelled) setAudienceDevices({ failed: true });
      });
    return () => { cancelled = true; };
  }, []);

  // 3. Subscribe to Broadcast history in Firestore
  useEffect(() => {
    const historyCol = collection(db, "notifications");
    const q = query(historyCol, orderBy("sentAt", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const logs = [];
      snapshot.forEach((doc) => {
        logs.push({ id: doc.id, ...doc.data() });
      });
      setHistory(logs);
      setHistoryLoading(false);
    }, (error) => {
      console.error("Error reading notification history:", error);
      setHistoryLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Devices the chosen audience reaches; undefined while still counting.
  const reach = targetSegment === "Dealers Only"
    ? audienceDevices?.dealers
    : targetSegment === "Tenants Only"
      ? audienceDevices?.tenants
      : devices?.total;

  let reachNote;
  if (typeof reach === "number") {
    reachNote = reach === 0
      ? { text: "No one in this audience has a device that can get pushes yet, so this would reach no one.", warn: true }
      : { text: `Reaches about ${formatCount(reach)} device${reach === 1 ? "" : "s"}.` };
  } else {
    reachNote = { text: audienceDevices?.failed ? "Couldn't count devices for this audience." : "Counting devices…" };
  }

  // Submitting the composer validates, then asks for confirmation; nothing is
  // sent until the confirmation is accepted (sendBroadcast).
  const handleBroadcast = (e) => {
    e.preventDefault();
    if (!campaignTitle.trim() || !body.trim()) {
      setStatusMsg({ text: "Please enter both a title and a message.", type: "error" });
      return;
    }
    setConfirmOpen(true);
  };

  const sendBroadcast = async () => {
    setConfirmOpen(false);
    setLoading(true);
    setStatusMsg({ text: "Sending notification...", type: "info" });

    try {
      // 1. Call HTTPS Callable Cloud Function for actual Push Notification dispatch
      // The Cloud Function handles both FCM push delivery AND writing to the
      // 'notifications' Firestore collection, so do NOT write here (it would duplicate).
      const sendNotificationFunc = httpsCallable(functions, "sendNotification");
      const result = await sendNotificationFunc({
        title: campaignTitle.trim(),
        body: body.trim(),
        targetSegment,
        deepLink: deepLink.trim()
      });

      // sentCount is the number of devices FCM actually accepted. It can be 0
      // when no user in the segment has a registered device; surface that
      // instead of masking it with the overall device total.
      const sent = result.data?.sentCount ?? 0;
      if (sent === 0) {
        setStatusMsg({
          text: `No devices matched "${targetSegment}", so no push was delivered. Confirm users in this segment have the app installed with notifications enabled.`,
          type: "warning"
        });
      } else {
        setStatusMsg({
          text: `Sent to ${formatCount(sent)} device${sent === 1 ? "" : "s"} in "${targetSegment}".`,
          type: "success"
        });
      }
      setCampaignTitle("");
      setBody("");
      setDeepLink("");
    } catch (fnError) {
      console.error("sendNotification failed, falling back to in-app-only post:", fnError);

      try {
        // Fallback: the push function is unreachable (not deployed / errored), so
        // post the broadcast to Firestore for in-app delivery only. This does NOT
        // send any push, so it is reported as a warning and never as a success, so
        // a broken/undeployed function is never mistaken for a working send.
        const notificationsCol = collection(db, "notifications");
        await addDoc(notificationsCol, {
          title: campaignTitle.trim(),
          body: body.trim(),
          targetSegment,
          deepLink: deepLink.trim(),
          sentAt: serverTimestamp(),
          recipientCount: 0,
          type: "broadcast",
          status: "IN_APP_ONLY"
        });
        setStatusMsg({
          text: "Push delivery failed: the notification function is unreachable. The message was posted to the in-app inbox only (no push sent). Deploy or redeploy Cloud Functions to restore push.",
          type: "warning"
        });
        setCampaignTitle("");
        setBody("");
        setDeepLink("");
      } catch (dbError) {
        console.error("Error fallback broadcasting:", dbError);
        setStatusMsg({ text: "Failed to send the notification. Please check database permissions.", type: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  // Copies a sent notification back into the composer to send it again.
  const copyToComposer = (log) => {
    setCampaignTitle(log.title || "");
    setBody((log.body || "").substring(0, MESSAGE_LIMIT));
    setDeepLink(log.deepLink || "");
    setTargetSegment(SEGMENTS.some((s) => s.value === log.targetSegment) ? log.targetSegment : "All Registered Users");
    setStatusMsg({ text: "Copied into the composer. Check it over, then send.", type: "info" });
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    composerRef.current?.scrollIntoView({ block: "start", behavior: reduceMotion ? "auto" : "smooth" });
    titleInputRef.current?.focus({ preventScroll: true });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "Pending...";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);

    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    if (isToday) {
      return `Today, ${hours}:${minutes}`;
    }

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const day = date.getDate();
    const month = months[date.getMonth()];
    return `${day} ${month}, ${hours}:${minutes}`;
  };

  // Devices a sent notification reached, from the count the function stored.
  const reachedCount = (log) => {
    if (typeof log.recipientCount === "number") return log.recipientCount;
    const parsed = parseInt(log.recipientCount, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const totalReached = history.reduce((sum, log) => sum + reachedCount(log), 0);
  const lastSent = history[0];

  // History status to chip style, label, and what the devices column shows.
  const describeStatus = (log) => {
    switch (log.status) {
      case "FAILED":
        return { chip: "chip-danger", label: "Failed", devices: "Not sent", devicesClass: "muted" };
      case "SCHEDULED":
        return { chip: "chip-warning", label: "Scheduled", devices: "Pending", devicesClass: "muted" };
      case "DRAFT":
        return { chip: "", label: "Draft", devices: "Not sent", devicesClass: "muted" };
      case "IN_APP_ONLY":
        return { chip: "chip-warning", label: "In-app only", devices: "No push", devicesClass: "muted" };
      case "SENT":
      case undefined:
        return { chip: "chip-success", label: "Sent", devices: formatCount(reachedCount(log)), devicesClass: "" };
      default:
        return { chip: "", label: log.status, devices: formatCount(reachedCount(log)), devicesClass: "" };
    }
  };

  const searchTerm = historySearch.trim().toLowerCase();
  const matchingHistory = searchTerm
    ? history.filter((log) => `${log.title || ""} ${log.body || ""}`.toLowerCase().includes(searchTerm))
    : history;
  const shownHistory = matchingHistory.slice(0, historyShown);
  const moreCount = Math.min(HISTORY_PAGE, matchingHistory.length - shownHistory.length);

  const titleLength = campaignTitle.length;
  const previewDate = new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });

  const historyHead = (
    <thead>
      <tr>
        <th scope="col">Notification</th>
        <th scope="col">Audience</th>
        <th scope="col">Sent</th>
        <th scope="col" className="num">Devices</th>
        <th scope="col">Status</th>
        <th scope="col" className="np-col-action"><span className="sr-only">Actions</span></th>
      </tr>
    </thead>
  );

  return (
    <div className="np-panel">
      <div className="stat-strip">
        <div className="stat">
          <span className="stat-label">Devices reached</span>
          <span className="stat-value">{formatCount(totalReached)}</span>
          <span className="stat-note">
            {history.length > 0
              ? `Across ${formatCount(history.length)} notification${history.length === 1 ? "" : "s"}`
              : "No notifications sent yet"}
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Push-ready devices</span>
          <span className="stat-value">{devices ? formatCount(devices.total) : "…"}</span>
          <span className="stat-note">
            {devices ? `${formatCount(devices.android)} Android, ${formatCount(devices.ios)} iOS` : "Counting devices"}
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Last sent</span>
          <span className="stat-value np-stat-time">{lastSent ? formatTime(lastSent.sentAt) : "Never"}</span>
          <span className="stat-note">
            {lastSent ? `To ${segmentLabel(lastSent.targetSegment).toLowerCase()}` : "Nothing has gone out yet"}
          </span>
        </div>
      </div>

      <section className="card np-composer" aria-labelledby="np-composer-title" ref={composerRef}>
        <div className="card-header">
          <div className="np-card-heading">
            <h2 className="card-title" id="np-composer-title">New notification</h2>
            <p className="np-card-desc">
              Sends a push to every registered device in the audience and posts it to the in-app inbox.
            </p>
          </div>
        </div>

        <div className="card-body np-composer-body">
          <form onSubmit={handleBroadcast} className="np-form">
            <div className="field">
              <span className="field-label" id="np-audience-label">Audience</span>
              <div className="segmented np-segmented" role="group" aria-labelledby="np-audience-label">
                {SEGMENTS.map((segment) => (
                  <button
                    key={segment.value}
                    type="button"
                    aria-pressed={targetSegment === segment.value}
                    onClick={() => setTargetSegment(segment.value)}
                    disabled={loading}
                  >
                    {segment.label}
                  </button>
                ))}
              </div>
              <span
                className={`field-hint np-reach ${reachNote.warn ? "is-warning" : ""}`}
                id="np-reach"
                aria-live="polite"
              >
                {reachNote.text}
              </span>
            </div>

            <div className="field">
              <div className="np-label-row">
                <label className="field-label" htmlFor="np-title">Title</label>
                <span
                  className={`np-count ${titleLength > TITLE_GUIDE ? "is-limit" : ""}`}
                  aria-live="polite"
                >
                  {titleLength}/{TITLE_GUIDE}
                </span>
              </div>
              <input
                id="np-title"
                ref={titleInputRef}
                className="input"
                type="text"
                placeholder="e.g. New homes near you this week"
                value={campaignTitle}
                onChange={(e) => setCampaignTitle(e.target.value)}
                aria-describedby="np-title-hint"
                required
                disabled={loading}
              />
              <span className={`field-hint ${titleLength > TITLE_GUIDE ? "np-hint-warning" : ""}`} id="np-title-hint">
                {titleLength > TITLE_GUIDE
                  ? "Phones may cut this title off. Shorter titles read in full on the lock screen."
                  : `Keep it under ${TITLE_GUIDE} characters so it isn't cut off on the lock screen.`}
              </span>
            </div>

            <div className="field">
              <div className="np-label-row">
                <label className="field-label" htmlFor="np-body">Message</label>
                <span
                  className={`np-count ${body.length >= MESSAGE_LIMIT ? "is-limit" : ""}`}
                  aria-live="polite"
                >
                  {body.length}/{MESSAGE_LIMIT}
                </span>
              </div>
              <textarea
                id="np-body"
                className="textarea"
                placeholder="What should people see in their notification tray?"
                value={body}
                onChange={(e) => setBody(e.target.value.substring(0, MESSAGE_LIMIT))}
                rows="4"
                required
                disabled={loading}
              />
              <span className="field-hint">Up to {MESSAGE_LIMIT} characters. Emoji are supported.</span>
            </div>

            <div className="field">
              <label className="field-label" htmlFor="np-link">Link</label>
              <div className="np-input-icon">
                <Link2 size={16} variant="Linear" aria-hidden="true" />
                <input
                  id="np-link"
                  className="input"
                  type="text"
                  placeholder="https://gharsetu.ai/..."
                  value={deepLink}
                  onChange={(e) => setDeepLink(e.target.value)}
                  disabled={loading}
                />
              </div>
              <span className="field-hint">
                Optional. Opens when someone taps this message in their notification list.
              </span>
            </div>

            {statusMsg.text && (
              <div
                className={`np-alert ${statusMsg.type}`}
                role={statusMsg.type === "error" ? "alert" : "status"}
              >
                {statusMsg.text}
              </div>
            )}

            <div className="np-actions">
              <span className="np-actions-note">
                You will confirm before anything is sent.
              </span>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? <span className="btn-spinner" aria-hidden="true" /> : <Send2 size={18} variant="Linear" />}
                {loading ? "Sending..." : "Send notification"}
              </button>
            </div>
          </form>

          <div className="np-preview">
            <span className="field-label">Preview</span>
            <div className="np-phone" aria-hidden="true">
              <div className="np-phone-screen">
                <div className="np-phone-clock">
                  <span className="np-phone-time">9:41</span>
                  <span className="np-phone-date">{previewDate}</span>
                </div>
                <div className="np-push">
                  <div className="np-push-head">
                    <img src="/logo.png" alt="" className="np-push-icon" />
                    <span className="np-push-app">GharSetu</span>
                    <span className="np-push-time">now</span>
                  </div>
                  <span className={`np-push-title ${campaignTitle.trim() ? "" : "is-placeholder"}`}>
                    {campaignTitle.trim() || "Notification title"}
                  </span>
                  <span className={`np-push-body ${body.trim() ? "" : "is-placeholder"}`}>
                    {body.trim() || "Your message appears here."}
                  </span>
                </div>
              </div>
            </div>
            <span className="field-hint np-preview-note">
              Sending to {segmentLabel(targetSegment).toLowerCase()}. Phones may shorten long text.
            </span>
          </div>
        </div>
      </section>

      <section className="np-history" aria-labelledby="np-history-title">
        <div className="np-history-head">
          <h2 className="section-title" id="np-history-title">Sent notifications</h2>
          {history.length > 0 && (
            <div className="search-field np-history-search">
              <SearchNormal1 size={16} variant="Linear" aria-hidden="true" />
              <input
                type="search"
                className="input"
                placeholder="Search titles and messages"
                aria-label="Search sent notifications"
                value={historySearch}
                onChange={(e) => {
                  setHistorySearch(e.target.value);
                  setHistoryShown(HISTORY_PAGE);
                }}
              />
            </div>
          )}
        </div>

        {historyLoading ? (
          <div className="table-wrap" aria-busy="true">
            <table className="table">
              {historyHead}
              <tbody>
                {[0, 1, 2, 3].map((i) => (
                  <tr key={i}>
                    <td><div className="skeleton np-skel-title" /><div className="skeleton np-skel-body" /></td>
                    <td><div className="skeleton np-skel-short" /></td>
                    <td><div className="skeleton np-skel-short" /></td>
                    <td className="num"><div className="skeleton np-skel-num" /></td>
                    <td><div className="skeleton np-skel-chip" /></td>
                    <td />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : history.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <span className="empty-state-icon" aria-hidden="true">
                <Notification size={22} variant="Linear" />
              </span>
              <h3>No notifications sent yet</h3>
              <p>Every notification you send from here is listed with its audience, time and the number of devices it reached.</p>
            </div>
          </div>
        ) : matchingHistory.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <span className="empty-state-icon" aria-hidden="true">
                <SearchNormal1 size={22} variant="Linear" />
              </span>
              <h3>No sent notifications match</h3>
              <p>Nothing sent has &ldquo;{historySearch.trim()}&rdquo; in its title or message.</p>
              <button type="button" className="btn-secondary btn-sm" onClick={() => setHistorySearch("")}>
                Clear search
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="table-wrap">
              <table className="table np-table">
                {historyHead}
                <tbody>
                  {shownHistory.map((log) => {
                    const status = describeStatus(log);
                    return (
                      <tr key={log.id}>
                        <td className="np-cell-main" data-label="Notification">
                          <span className="np-row-title">{log.title}</span>
                          {log.body && <span className="np-row-body">{log.body}</span>}
                          {log.deepLink && (
                            <span className="np-row-link" title={log.deepLink}>
                              <Link2 size={12} variant="Linear" aria-hidden="true" />
                              <span className="np-row-link-text">{log.deepLink}</span>
                            </span>
                          )}
                        </td>
                        <td data-label="Audience">{segmentLabel(log.targetSegment)}</td>
                        <td data-label="Sent" className="np-nowrap">{formatTime(log.sentAt)}</td>
                        <td data-label="Devices" className={`num ${status.devicesClass}`}>{status.devices}</td>
                        <td data-label="Status">
                          <span className={`chip chip-dot ${status.chip}`}>{status.label}</span>
                        </td>
                        <td className="np-col-action">
                          <button
                            type="button"
                            className="btn-ghost btn-sm np-reuse"
                            onClick={() => copyToComposer(log)}
                            disabled={loading}
                            aria-label={`Use "${log.title || "this notification"}" again`}
                          >
                            <Repeat size={16} variant="Linear" aria-hidden="true" />
                            Use again
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="np-history-foot">
              <span className="muted">
                Showing {formatCount(shownHistory.length)} of {formatCount(matchingHistory.length)}
              </span>
              {moreCount > 0 && (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => setHistoryShown((n) => n + HISTORY_PAGE)}
                >
                  Show {moreCount} more
                </button>
              )}
            </div>
          </>
        )}
      </section>

      {confirmOpen && (
        <div className="modal-overlay">
          <div
            className="modal np-confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="np-confirm-title"
            aria-describedby="np-confirm-desc"
          >
            <div className="modal-header">
              <h2 id="np-confirm-title">Send this notification?</h2>
              <button
                type="button"
                className="btn-icon"
                onClick={() => setConfirmOpen(false)}
                aria-label="Close"
              >
                <Add size={22} variant="Linear" className="np-close-icon" />
              </button>
            </div>
            <div className="modal-body">
              <p id="np-confirm-desc" className="np-confirm-desc">
                It goes out as a push to <strong>{segmentLabel(targetSegment).toLowerCase()}</strong> right away. A sent push cannot be recalled.
              </p>
              <dl className="np-summary">
                <div>
                  <dt>Audience</dt>
                  <dd>{segmentLabel(targetSegment)}</dd>
                </div>
                <div>
                  <dt>Devices</dt>
                  <dd>{typeof reach === "number" ? `About ${formatCount(reach)}` : "Still counting"}</dd>
                </div>
                <div>
                  <dt>Title</dt>
                  <dd>{campaignTitle.trim()}</dd>
                </div>
                <div>
                  <dt>Message</dt>
                  <dd>{body.trim()}</dd>
                </div>
                {deepLink.trim() && (
                  <div>
                    <dt>Link</dt>
                    <dd className="np-summary-link">{deepLink.trim()}</dd>
                  </div>
                )}
              </dl>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setConfirmOpen(false)}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={sendBroadcast} autoFocus>
                <Send2 size={18} variant="Linear" />
                Send notification
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
